<script lang="ts">
  import { onMount } from 'svelte';
  import { Chart, type ChartConfiguration } from 'chart.js/auto';

  let chartCanvas: HTMLCanvasElement;
  let chart: Chart;

  onMount(() => {
    const ctx = chartCanvas.getContext('2d')!;

    const config: ChartConfiguration = {
      type: 'doughnut',
      data: {
        labels: ['Groceries', 'Dining', 'Transportation', 'Entertainment', 'Utilities', 'Other'],
        datasets: [{
          data: [450, 320, 180, 150, 200, 100],
          backgroundColor: [
            '#2563eb',
            '#10b981',
            '#f59e0b',
            '#ec4899',
            '#8b5cf6',
            '#6b7280',
          ],
          borderWidth: 0,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                return `${label}: $${value}`;
              },
            },
          },
        },
        cutout: '70%',
      },
    };

    chart = new Chart(ctx, config);

    return () => {
      chart.destroy();
    };
  });
</script>

<div class="demo-container">
  <div class="demo-header">
    <h3>Monthly Budget Overview</h3>
    <div class="demo-badge">Sample Data</div>
  </div>
  <div class="chart-wrapper">
    <canvas bind:this={chartCanvas}></canvas>
    <div class="chart-center">
      <div class="total-amount">$1,400</div>
      <div class="total-label">Total Spent</div>
    </div>
  </div>
  <div class="demo-legend">
    <div class="legend-item">
      <span class="legend-dot" style="background: #2563eb;"></span>
      <span class="legend-label">Groceries</span>
      <span class="legend-value">$450</span>
    </div>
    <div class="legend-item">
      <span class="legend-dot" style="background: #10b981;"></span>
      <span class="legend-label">Dining</span>
      <span class="legend-value">$320</span>
    </div>
    <div class="legend-item">
      <span class="legend-dot" style="background: #f59e0b;"></span>
      <span class="legend-label">Transportation</span>
      <span class="legend-value">$180</span>
    </div>
    <div class="legend-item">
      <span class="legend-dot" style="background: #ec4899;"></span>
      <span class="legend-label">Entertainment</span>
      <span class="legend-value">$150</span>
    </div>
  </div>
</div>

<style>
  .demo-container {
    background: var(--color-background);
    border-radius: var(--radius-2xl);
    padding: var(--spacing-xl);
    box-shadow: var(--shadow-xl);
    border: 1px solid var(--color-border-light);
  }

  .demo-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-xl);
  }

  .demo-header h3 {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    margin: 0;
  }

  .demo-badge {
    background: var(--color-primary-light);
    color: var(--color-primary);
    padding: var(--spacing-xs) var(--spacing-md);
    border-radius: var(--radius-full);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
  }

  .chart-wrapper {
    position: relative;
    max-width: 300px;
    margin: 0 auto var(--spacing-xl);
  }

  .chart-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }

  .total-amount {
    font-size: var(--font-size-3xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
  }

  .total-label {
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }

  .demo-legend {
    display: grid;
    gap: var(--spacing-sm);
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm);
    border-radius: var(--radius-md);
    transition: background var(--transition-fast);
  }

  .legend-item:hover {
    background: var(--color-background-alt);
  }

  .legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .legend-label {
    flex: 1;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
  }

  .legend-value {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
  }
</style>
